'use client';

import {Scroller} from '@shared/Scroller/Scroller';
import classNames from '@sindresorhus/class-names';
import {chunk} from '@utils/chunk';
import Image from 'next/image';
import Link from 'next/link';

import {TCategories} from '@web-landing/api/fetchCategories';

import {MainLanding} from '@strapi/types';

import {useLocale} from '@utils/hooks/useLocale';

import {logTmrEvent} from '@web-landing/analytics/helpers/logTmrEvent';

import styles from './Categories.module.scss';
import {getMappedTranslates} from './categoriesTranslates';
import {imagesMap} from './imagesMap';

type Props = {
  mode: 'default' | 'colored';
  type: 'games' | 'apps';
  categories: TCategories;
} & Pick<MainLanding, 'webCatalogCategories'>;

export const Categories = ({
  categories,
  mode,
  type,
  webCatalogCategories,
}: Props) => {
  const locale = useLocale();
  const dataMap = {
    apps: {
      title: webCatalogCategories?.appsTitle,
      button: webCatalogCategories?.appsButton,
      categoriesArray:
        locale !== 'ru'
          ? getMappedTranslates(categories.content, 'apps', locale)
          : categories.content,
    },
    games: {
      title: webCatalogCategories?.gamesTitle,
      button: webCatalogCategories?.gamesButton,
      categoriesArray:
        locale !== 'ru'
          ? getMappedTranslates(categories.contentGames, 'games', locale)
          : categories.contentGames,
    },
  };

  const {button, categoriesArray, title} = dataMap[type];
  const renderCategories = categoriesArray.filter((item) => item.appCount > 0);
  const chunksLength = Math.round(renderCategories.length / 2);
  const isAllPropsExists = button && renderCategories.length && title;

  const onSwipe = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      return logTmrEvent('arrowRight.click', {compilation_name: title});
    }
    return logTmrEvent('arrowLeft.click', {compilation_name: title});
  };

  const onCategoryClick = (categoryName: string) => {
    if (type === 'apps') {
      return logTmrEvent('landing.appsCategory.click', {
        compilation_name: title,
        category_name: categoryName,
      });
    }
    return logTmrEvent('landing.gamesCategory.click', {
      compilation_name: title,
      category_name: categoryName,
    });
  };

  const onGoAllClick = () => {
    logTmrEvent('landing.click', {
      button_text: button,
      compilation_name: title,
    });
  };

  if (!isAllPropsExists) return null;

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.wrapper__transparent]: mode === 'default',
      })}
    >
      <h2 className={styles.title}>{title}</h2>
      <Link
        className={styles.button}
        href={type === 'games' ? '/catalog/games' : '/catalog'}
        prefetch={false}
        onClick={onGoAllClick}
      >
        {button}
      </Link>
      <div className={styles.content}>
        <Scroller
          variant="grid"
          classes={{scrollable: styles.scroller}}
          disableDefaultScrollableContainerClass
          navButtonsScrollBy={300}
          onSwipe={onSwipe}
        >
          {chunk(renderCategories, chunksLength).map((subArr, index) => (
            <div className={styles.categories} key={index}>
              {subArr.map((category) => (
                <Link
                  key={category.category}
                  className={classNames(styles.category, {
                    [styles.category__colored]: mode === 'colored',
                    [styles[`category__colored_${category?.iconColor}`]]:
                      mode === 'colored',
                  })}
                  onClick={() => onCategoryClick(category.category)}
                  prefetch={false}
                  href={
                    type === 'apps'
                      ? `/catalog/${category.category.toLowerCase()}`
                      : `/catalog/games/${category.category.toLowerCase()}`
                  }
                >
                  {mode === 'default' && (
                    <Image
                      src={category.iconUrl}
                      width={28}
                      height={28}
                      className={classNames(
                        styles.icon,
                        styles[category?.iconColor as string],
                      )}
                      alt=""
                    />
                  )}
                  {category.categoryName}
                  {mode === 'colored' &&
                    imagesMap?.[type]?.[category?.category] && (
                      <img
                        className={styles.coloredImage}
                        alt=""
                        src={imagesMap[type][category?.category]}
                      />
                    )}
                </Link>
              ))}
            </div>
          ))}
        </Scroller>
      </div>
    </div>
  );
};
